/**
 * ScreenGate - Per-screen permission wrapper.
 *
 * Usage:
 *   <ScreenGate screen="AdminDashboard"><AdminDashboard /></ScreenGate>
 *
 * Çocuğu render etmeden önce `useAuth().canAccess(screen)` kontrolü yapar.
 * Yetkisi yoksa AccessDenied render eder. Auth/role kontrolü hâlâ
 * üst seviyedeki `<ProtectedRoute>` tarafından yapılır.
 */

import React from 'react';
import { useAuth } from '@/api/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ShieldOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ErrorBoundary from '@/components/ErrorBoundary';

export default function ScreenGate({ screen, children }) {
  const { canAccess } = useAuth();

  if (!screen) return children;

  if (!canAccess(screen)) {
    return <AccessDenied screenName={screen} />;
  }

  // Ekran içeriğini ErrorBoundary ile sar: bir sayfa çökse bile üst
  // navigasyon/menü ayakta kalır, kullanıcı başka ekrana geçebilir.
  // key={screen} → farklı ekrana geçince boundary sıfırlanır (takılı kalmaz).
  return (
    <ErrorBoundary key={screen} label="Dieser Bereich">
      {children}
    </ErrorBoundary>
  );
}

function AccessDenied({ screenName }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-[60vh] flex items-center justify-center bg-slate-50 p-6">
      <div className="max-w-md text-center">
        <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
          <ShieldOff className="w-8 h-8 text-amber-600" />
        </div>
        <h1 className="text-xl font-bold text-slate-900 mb-2">Kein Zugriff</h1>
        <p className="text-sm text-slate-600 mb-2">
          Sie haben keine Berechtigung für diesen Bereich.
        </p>
        <p className="text-xs text-slate-400 mb-6">
          Bitte wenden Sie sich an Ihren Administrator.
        </p>
        <Button onClick={() => navigate(-1)} variant="outline" className="min-h-[44px]">
          Zurück
        </Button>
      </div>
    </div>
  );
}
